import { useCallback, useState, useRef, useEffect, useMemo } from 'react'
import gsap from 'gsap'
import cn from 'classnames'
import ChevronDownIcon from '../icons/chevron-down.svg'
import { createUseStyles } from '../helpers/createStyles'
import { theme } from '../styles/theme'

const Accordion = ({
  className,
  label,
  children,
  expanded: controlExpanded,
  setExpanded: controlSetExpanded,
  noHorizontalPadding,
  hideCollapseButton
}) => {
  const styles = useStyles()
  const ref = useRef()
  const [internalExpanded, setInternalExpanded] = useState(false)

  const handleToggle = useCallback(() => {
    if (typeof controlSetExpanded === 'function') {
      controlSetExpanded(value => !value)
    } else {
      setInternalExpanded(value => !value)
    }
  }, [])

  const expanded = useMemo(
    () =>
      typeof controlExpanded === 'boolean' ? controlExpanded : internalExpanded,
    [internalExpanded, controlExpanded]
  )

  useEffect(() => {
    if (ref.current) {
      gsap.to(ref.current, {
        height: expanded ? 'auto' : 0,
        pointerEvents: expanded ? 'all' : 'none',
        duration: 0.25
      })
      gsap.to(ref.current.children, {
        opacity: expanded ? 1 : 0,
        y: expanded ? 0 : -10,
        duration: 0.25,
        stagger: 0.03
      })
    }
  }, [expanded])

  return (
    <div className={cn(styles.accordion, noHorizontalPadding && 'noHorizontalPadding', className || null, Array.isArray(className) ? className.map(item => styles[item]) : '')}>
      <button
        type='button'
        className={cn(styles.accordionBtn, 'accordionBtn', noHorizontalPadding && 'noHorizontalPadding')}
        onClick={handleToggle}
        aria-label={`${expanded ? 'Hide' : 'Reveal'} answer to: ${label}`}
      >
        <span className={styles.label}>{label}</span>
        <div className={cn(styles.icon, expanded && 'expanded', noHorizontalPadding && 'noHorizontalPadding', hideCollapseButton && styles.hideCollapseButton)}>
          <ChevronDownIcon />
        </div>
      </button>
      <div ref={ref} className={cn(styles.hiddenContent, { expanded }, 'accordionContent', noHorizontalPadding && 'noHorizontalPadding')}>
        <div className={styles.container}>{children}</div>
      </div>
    </div>
  )
}

const useStyles = createUseStyles({

  shade4: {
    ' & .accordionBtn': {
      backgroundColor: '#AFAC9C'
    },
    ' & .accordionContent': {
      backgroundColor: '#AFAC9C'
    }
  },
  shade3: {
    ' & .accordionBtn': {
      backgroundColor: '#E0DED2'
    },
    ' & .accordionContent': {
      backgroundColor: '#E0DED2'
    }
  },
  shade2: {
    ' & .accordionBtn': {
      backgroundColor: '#E0DED2'
    },
    ' & .accordionContent': {
      backgroundColor: '#F8F7EE'
    }
  },
  shade1: {
    ' & .accordionBtn': {
      backgroundColor: '#F8F7EE'
    },
    ' & .accordionContent': {
      backgroundColor: '#F8F7EE'
    }
  },
  shade0: {
    ' & .accordionBtn': {
      backgroundColor: theme.colors.white
    },
    ' & .accordionContent': {
      backgroundColor: theme.colors.white
    }
  },

  tepAccordion: {
    ' & .accordionBtn': {
      color: theme.colors.black,
      textAlign: 'left',
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(4),

      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),

      [theme.breakpoints.up('md')]: {
        paddingLeft: theme.spacing(12),
        paddingRight: theme.spacing(12)
      },

      '&.noHorizontalPadding': {
        paddingLeft: '0px !important',
        '& *': {
          marginLeft: '0px !important'
        }
      }

    },
    ' & .accordionContent': {
      padding: 0,
      transition: 'padding 0.3s ease',
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      [theme.breakpoints.up('md')]: {
        paddingLeft: theme.spacing(12),
        paddingRight: theme.spacing(12)
      },

      '&.expanded': {
        paddingTop: 50,
        paddingBottom: 50,
        // borderBottom: '1px solid black',
        borderTop: '1px solid black',

        overflow: 'visible',
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        '-ms-overflow-style': 'none', /* IE and Edge */
        'scrollbar-width': 'none', /* Firefox */

        [theme.breakpoints.up('md')]: {
          overflow: 'hidden'
        }

      },
      '&.noHorizontalPadding': {
        paddingLeft: '0px !important',
        '& *': {
          marginLeft: '0px !important'
        }
      }
    },

    // Add a counter to the nested accordion button
    ' & .accordionContent .accordionBtn::before': {
      counterIncrement: 'tepLandingAccordions',
      content: 'counter(tepLandingAccordions, decimal-leading-zero)',
      color: theme.colors.black,
      fontFamily: 'Söhne Leicht',
      fontSize: 14,
      fontWeight: 400,
      marginRight: theme.spacing(2),
      [theme.breakpoints.up('md')]: {
        position: 'absolute'
      }
    },

    ' & .tepAccordion': { // If The accordion contains another accordion

      paddingBottom: theme.spacing(0),

      marginLeft: theme.spacing(-4), // prevent them from appearing indented.
      marginRight: theme.spacing(-4),

      [theme.breakpoints.up('md')]: {
        marginLeft: theme.spacing(-12), // prevent them from appearing indented.
        marginRight: theme.spacing(-12)
      },

      ' & > .accordionBtn': {
        borderTop: '1px solid black'
      },

      ' & .accordionContent': {
        '&.expanded': {
          borderBottom: 'none'
        }
      }

    },

    '& .tepAccordion:last-child ': {
      borderBottom: '1px solid black'
    },

    '&.partnersAccordion .accordionContent': {
      paddingLeft: 0,
      paddingRight: 0,
      [theme.breakpoints.up('md')]: {
        paddingLeft: 0,
        paddingRight: 0
      }
    }

  },

  noBottomPadding: {
    '& > .accordionContent:first-of-type': { // Use the '>' selector to target only direct children
      paddingBottom: '0px !important',
      '& > div:first-of-type': { // Ensure it only applies to direct child divs
        paddingBottom: 0
      }
    }
  },
  noTopPadding: {
    '& > .accordionContent:first-of-type': { // Use the '>' selector to target only direct children
      paddingTop: '0px !important',
      '& > div:first-of-type': { // Ensure it only applies to direct child divs
        paddingTop: 0
      }
    }
  },
  noTopPaddingDesktop: {
    [theme.breakpoints.up('md')]: {
      '& > .accordionContent:first-of-type': { // Use the '>' selector to target only direct children
        paddingTop: '0px !important',
        '& > div:first-of-type': { // Ensure it only applies to direct child divs
          paddingTop: 0
        }
      }
    }
  },

  accordion: {},
  accordionBtn: {
    width: '100%',
    fontSize: 18,
    lineHeight: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${theme.spacing(2)}px 0px`,
    '& > span': {
      [theme.breakpoints.up('md')]: {
        marginLeft: '10px'
      }
    }
  },
  hideCollapseButton: {
    visibility: 'hidden'
  },
  label: {
    width: '100%',
    justifyContent: 'left',
    marginBottom: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
      justifyContent: 'center'
    }
  },
  icon: {
    marginLeft: theme.spacing(1),
    flexShrink: 0,
    flexGrow: 0,
    '&.expanded': {
      svg: {
        transform: 'rotateX(-180deg) !important'
      }
    },
    '& svg': {
      display: 'block',
      width: 10,
      height: 5,
      transition: 'transform 0.3s ease',
      transform: 'rotateX(0deg)',
      [theme.breakpoints.up('md')]: {
        width: 10,
        height: 5
      }
    }
  },
  hiddenContent: {
    height: 0,
    pointerEvents: 'none',
    overflow: 'hidden'
  },
  container: {
    padding: `0px 0px ${theme.spacing(2)}px `
  }
})

export default Accordion
